import { useState, useEffect, useRef } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Alert, InputGroup } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { mohApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'
import { useAuthStore } from '../store/authStore'

interface MusicClass {
  id: string
  tenant_id: string | null
  name: string
  mode: string
  directory: string
  stream_url: string | null
  stream_format: string | null
  random: boolean
  sort: string
  is_default: boolean
  is_active: boolean
  files_count?: number
}

interface MohFile {
  order: number
  filename: string
  path: string
  size: number
  size_human: string
  modified: string
  format: string
  duration: number | null
  duration_human: string | null
}

interface MohFilesResponse {
  moh_class: string
  directory: string
  mode: string
  random_playback: boolean
  sort_order: string
  total_files: number
  total_duration: number
  total_duration_human: string
  files: MohFile[]
}

const emptyForm = {
  name: '',
  mode: 'files',
  stream_url: '',
  stream_format: 'mp3',
  random: true,
  sort: 'alpha',
  is_default: false,
  is_active: true
}

const modes = [
  { value: 'files', label: 'Dateien', description: 'Lokale Audio-Dateien abspielen' },
  { value: 'mp3', label: 'MP3', description: 'MP3-Dateien (benötigt mpg123)' },
  { value: 'stream', label: 'Internet Radio', description: 'Stream von URL abspielen' },
  { value: 'custom', label: 'Benutzerdefiniert', description: 'Eigene Anwendung' }
]

const sortModes = [
  { value: 'alpha', label: 'Alphabetisch' },
  { value: 'random', label: 'Zufällig' },
  { value: 'randstart', label: 'Zufälliger Start' }
]

const streamFormats = [
  { value: 'ulaw', label: 'G.711 u-law (8kHz)' },
  { value: 'alaw', label: 'G.711 a-law (8kHz)' },
  { value: 'slin', label: 'Signed Linear (8kHz)' },
  { value: 'slin16', label: 'Signed Linear (16kHz)' },
  { value: 'mp3', label: 'MP3 (benötigt mpg123)' }
]

// Beispiel-Streams für Internet Radio
const exampleStreams = [
  { name: 'Klassik Radio', url: 'http://stream.klassikradio.de/live/mp3-128' },
  { name: 'SWR3', url: 'https://liveradio.swr.de/sw282p3/swr3/play.mp3' },
  { name: 'Bayern 3', url: 'https://streams.br.de/bayern3_2.m3u' },
  { name: 'Jazz Radio', url: 'http://jazzradio.ice.infomaniak.ch/jazzradio-high.mp3' },
  { name: 'Lounge Radio', url: 'http://streaming.radionomy.com/Lounge-radio' }
]

export default function MusicOnHold() {
  const confirm = useConfirm()
  const isSuperAdmin = useAuthStore(state => state.user?.role === 'super_admin')
  const [classes, setClasses] = useState<MusicClass[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingClass, setEditingClass] = useState<MusicClass | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [testingStream, setTestingStream] = useState(false)
  const [showFilesModal, setShowFilesModal] = useState(false)
  const [filesData, setFilesData] = useState<MohFilesResponse | null>(null)
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [currentMohId, setCurrentMohId] = useState<string | null>(null)
  const [currentClassProtected, setCurrentClassProtected] = useState(false)
  const [playingFile, setPlayingFile] = useState<string | null>(null)
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    loadClasses()
  }, [])

  const loadClasses = async () => {
    try {
      const response = await mohApi.list()
      setClasses(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const loadFiles = async (cls: MusicClass) => {
    if (cls.mode === 'stream') {
      toast.info('Streams haben keine Dateien')
      return
    }
    setLoadingFiles(true)
    setShowFilesModal(true)
    setCurrentMohId(cls.id)
    setCurrentClassProtected(!isSuperAdmin && cls.tenant_id === null)
    setPlayingFile(null)
    if (audioRef) {
      audioRef.pause()
      setAudioRef(null)
    }
    try {
      const response = await mohApi.listFiles(cls.id)
      setFilesData(response.data)
    } catch {
      toast.error('Fehler beim Laden der Dateien')
      setShowFilesModal(false)
    } finally {
      setLoadingFiles(false)
    }
  }

  const getAuthToken = (): string | null => {
    try {
      const authStorage = localStorage.getItem('auth-storage')
      if (authStorage) {
        const parsed = JSON.parse(authStorage)
        return parsed?.state?.token || null
      }
    } catch {
      // ignore
    }
    return null
  }

  const playFile = (filename: string) => {
    if (!currentMohId) return

    // Stop current playback if any
    if (audioRef) {
      audioRef.pause()
    }

    if (playingFile === filename) {
      // Toggle off
      setPlayingFile(null)
      setAudioRef(null)
      return
    }

    // Create new audio element with auth token
    const token = getAuthToken()
    const url = mohApi.getPlayUrl(currentMohId, filename)

    if (!token) {
      toast.error('Authentifizierungstoken nicht gefunden')
      return
    }

    const audio = new Audio()
    audio.src = url + `?token=${encodeURIComponent(token)}`

    audio.onended = () => {
      setPlayingFile(null)
      setAudioRef(null)
    }
    audio.onerror = (e) => {
      console.error('Audio error:', e, audio.error)
      const errorMsg = audio.error ? `Fehler: ${audio.error.message || audio.error.code}` : 'Fehler beim Abspielen der Datei'
      toast.error(errorMsg)
      setPlayingFile(null)
      setAudioRef(null)
    }

    audio.play().then(() => {
      setPlayingFile(filename)
      setAudioRef(audio)
    }).catch((err) => {
      console.error('Play error:', err)
      toast.error(`Fehler beim Abspielen: ${err.message || 'Unbekannter Fehler'}`)
    })
  }

  const closeFilesModal = () => {
    if (audioRef) {
      audioRef.pause()
      setAudioRef(null)
    }
    setPlayingFile(null)
    setShowFilesModal(false)
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0 || !currentMohId) return

    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        await mohApi.uploadFile(currentMohId, file)
        toast.success(`${file.name} hochgeladen`)
      }
      // Reload files list
      const response = await mohApi.listFiles(currentMohId)
      setFilesData(response.data)
    } catch (error) {
      toast.error('Fehler beim Hochladen')
    } finally {
      setUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDeleteFile = async (filename: string) => {
    if (!currentMohId) return

    const confirmed = await confirm({
      title: 'Datei löschen',
      message: `Datei "${filename}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return

    try {
      await mohApi.deleteFile(currentMohId, filename)
      toast.success('Datei gelöscht')
      // Reload files list
      const response = await mohApi.listFiles(currentMohId)
      setFilesData(response.data)
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const openCreateModal = () => {
    setEditingClass(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (cls: MusicClass) => {
    setEditingClass(cls)
    setFormData({
      name: cls.name,
      mode: cls.mode || 'files',
      stream_url: cls.stream_url || '',
      stream_format: cls.stream_format || 'mp3',
      random: cls.random ?? true,
      sort: cls.sort || 'alpha',
      is_default: cls.is_default ?? false,
      is_active: cls.is_active
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validierung
    if (formData.mode === 'stream' && !formData.stream_url) {
      toast.error('Stream-URL ist erforderlich für Internet Radio')
      return
    }

    try {
      const payload = {
        name: formData.name,
        mode: formData.mode,
        random: formData.random,
        sort: formData.sort,
        is_default: formData.is_default,
        is_active: formData.is_active,
        stream_url: formData.mode === 'stream' ? formData.stream_url : null,
        stream_format: formData.mode === 'stream' ? formData.stream_format : null
      }

      if (editingClass) {
        await mohApi.update(editingClass.id, payload)
        toast.success('Musikklasse aktualisiert')
      } else {
        await mohApi.create(payload)
        toast.success('Musikklasse erstellt')
      }
      setShowModal(false)
      loadClasses()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDelete = async (cls: MusicClass) => {
    if (cls.is_default) {
      toast.error('Standard-Klasse kann nicht gelöscht werden')
      return
    }
    const confirmed = await confirm({
      title: 'Musikklasse löschen',
      message: `Musikklasse "${cls.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await mohApi.delete(cls.id)
      toast.success('Musikklasse gelöscht')
      loadClasses()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const testStream = async () => {
    if (!formData.stream_url) {
      toast.error('Bitte geben Sie eine Stream-URL ein')
      return
    }
    setTestingStream(true)
    try {
      // Simple URL validation
      new URL(formData.stream_url)
      toast.success('Stream-URL scheint gültig zu sein')
    } catch {
      toast.error('Ungültige URL')
    } finally {
      setTestingStream(false)
    }
  }

  const selectExampleStream = (url: string) => {
    setFormData({ ...formData, stream_url: url })
  }

  const getModeLabel = (mode: string) => {
    return modes.find(m => m.value === mode)?.label || mode
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Wartemusik (Music on Hold)</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neue Musikklasse
        </Button>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Modus</th>
              <th>Quelle</th>
              <th>Dateien</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-4">Laden...</td></tr>
            ) : classes.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-4 text-muted">Keine Musikklassen</td></tr>
            ) : (
              classes.map(c => {
                const isProtected = !isSuperAdmin && c.tenant_id === null
                return (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                    {c.is_default && <Badge bg="primary" className="ms-2">Standard</Badge>}
                    {c.tenant_id === null && <Badge bg="secondary" className="ms-2">Global</Badge>}
                  </td>
                  <td>
                    <Badge bg={c.mode === 'stream' ? 'info' : 'secondary'}>
                      {c.mode === 'stream' && <i className="bi bi-broadcast me-1"></i>}
                      {getModeLabel(c.mode)}
                    </Badge>
                  </td>
                  <td>
                    {c.mode === 'stream' ? (
                      <small className="text-truncate d-inline-block" style={{ maxWidth: '200px' }} title={c.stream_url || ''}>
                        <i className="bi bi-globe me-1"></i>
                        {c.stream_url || '-'}
                      </small>
                    ) : (
                      <code className="small">{c.directory || '-'}</code>
                    )}
                  </td>
                  <td>{c.mode !== 'stream' ? (c.files_count ?? '-') : <i className="bi bi-infinity"></i>}</td>
                  <td>
                    <Badge bg={c.is_active ? 'success' : 'secondary'}>
                      {c.is_active ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </td>
                  <td className="text-end">
                    {c.mode !== 'stream' && (
                      <Button variant="outline-info" size="sm" className="me-1" onClick={() => loadFiles(c)} title="Dateien anzeigen">
                        <i className="bi bi-folder-fill"></i>
                      </Button>
                    )}
                    {!isProtected && (
                      <>
                        <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(c)}>
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(c)}
                          disabled={c.is_default}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
                )
              })
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingClass ? 'Musikklasse bearbeiten' : 'Neue Musikklasse'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="z.B. klassik, jazz, radio"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Modus *</Form.Label>
                  <Form.Select
                    value={formData.mode}
                    onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                  >
                    {modes.map(m => (
                      <option key={m.value} value={m.value}>{m.label} - {m.description}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {formData.mode === 'stream' ? (
              <>
                <Alert variant="info" className="mb-3">
                  <i className="bi bi-broadcast me-2"></i>
                  <strong>Internet Radio Stream</strong><br />
                  <small>Geben Sie die URL eines Audio-Streams ein. Der Stream wird in Echtzeit abgespielt.</small>
                </Alert>

                <Form.Group className="mb-3">
                  <Form.Label>Stream-URL *</Form.Label>
                  <InputGroup>
                    <Form.Control
                      value={formData.stream_url}
                      onChange={(e) => setFormData({ ...formData, stream_url: e.target.value })}
                      placeholder="http://stream.example.com/live.mp3"
                      required={formData.mode === 'stream'}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={testStream}
                      disabled={testingStream}
                    >
                      {testingStream ? <i className="bi bi-hourglass-split"></i> : <i className="bi bi-play-circle"></i>}
                      {' '}Testen
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    HTTP/HTTPS-URL zum Audio-Stream (MP3, AAC, etc.)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Beispiel-Streams</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {exampleStreams.map((stream, idx) => (
                      <Button
                        key={idx}
                        variant="outline-info"
                        size="sm"
                        onClick={() => selectExampleStream(stream.url)}
                      >
                        <i className="bi bi-music-note me-1"></i>
                        {stream.name}
                      </Button>
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Stream-Format</Form.Label>
                  <Form.Select
                    value={formData.stream_format}
                    onChange={(e) => setFormData({ ...formData, stream_format: e.target.value })}
                  >
                    {streamFormats.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Format für die Audiokonvertierung
                  </Form.Text>
                </Form.Group>
              </>
            ) : (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sortierung</Form.Label>
                      <Form.Select
                        value={formData.sort}
                        onChange={(e) => setFormData({ ...formData, sort: e.target.value })}
                      >
                        {sortModes.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3 pt-4">
                      <Form.Check
                        type="switch"
                        id="random"
                        label="Zufällige Wiedergabe"
                        checked={formData.random}
                        onChange={(e) => setFormData({ ...formData, random: e.target.checked })}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            <hr />

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="is_default"
                    label="Als Standard-Klasse"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  />
                  <Form.Text className="text-muted">
                    Wird verwendet, wenn keine spezifische Klasse angegeben ist
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="is_active"
                    label="Aktiv"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingClass ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Files Modal */}
      <Modal show={showFilesModal} onHide={closeFilesModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-music-note-list me-2"></i>
            Dateien: {filesData?.moh_class || ''}
          </Modal.Title>
        </Modal.Header>
        <div className="px-3 pt-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".wav,.mp3,.gsm,.ogg,.ulaw,.alaw"
            multiple
            style={{ display: 'none' }}
          />
          {!currentClassProtected && (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Hochladen...
                  </>
                ) : (
                  <>
                    <i className="bi bi-cloud-upload me-1"></i>
                    Datei hochladen
                  </>
                )}
              </Button>
              <small className="text-muted ms-2">
                Erlaubt: WAV, MP3, GSM, OGG
              </small>
            </>
          )}
        </div>
        <Modal.Body>
          {loadingFiles ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Laden...</span>
              </div>
              <p className="mt-2 text-muted">Dateien werden geladen...</p>
            </div>
          ) : filesData ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <div>
                  <small className="text-muted">
                    <i className="bi bi-folder me-1"></i>
                    <code>{filesData.directory}</code>
                  </small>
                </div>
                <div>
                  <Badge bg="secondary" className="me-2">
                    {filesData.total_files} Dateien
                  </Badge>
                  <Badge bg="info">
                    <i className="bi bi-clock me-1"></i>
                    {filesData.total_duration_human}
                  </Badge>
                </div>
              </div>

              <Alert variant="light" className="mb-3 py-2">
                <small>
                  <strong>Wiedergabe:</strong>{' '}
                  {filesData.random_playback ? (
                    <span><i className="bi bi-shuffle me-1"></i>Zufällige Reihenfolge</span>
                  ) : (
                    <span><i className="bi bi-list-ol me-1"></i>Sortiert ({filesData.sort_order})</span>
                  )}
                </small>
              </Alert>

              {filesData.files.length === 0 ? (
                <Alert variant="warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Keine Audio-Dateien in diesem Verzeichnis gefunden.
                </Alert>
              ) : (
                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>#</th>
                      <th style={{ width: '90px' }}></th>
                      <th>Dateiname</th>
                      <th style={{ width: '80px' }}>Format</th>
                      <th style={{ width: '80px' }}>Dauer</th>
                      <th style={{ width: '80px' }}>Größe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filesData.files.map((file) => (
                      <tr key={file.filename} className={playingFile === file.filename ? 'table-primary' : ''}>
                        <td className="text-muted">{file.order}</td>
                        <td>
                          <Button
                            variant={playingFile === file.filename ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => playFile(file.filename)}
                            title={playingFile === file.filename ? 'Stoppen' : 'Abspielen'}
                            className="me-1"
                          >
                            <i className={`bi ${playingFile === file.filename ? 'bi-stop-fill' : 'bi-play-fill'}`}></i>
                          </Button>
                          {!currentClassProtected && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteFile(file.filename)}
                              title="Löschen"
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          )}
                        </td>
                        <td>
                          <i className="bi bi-file-earmark-music me-2 text-primary"></i>
                          {file.filename}
                        </td>
                        <td>
                          <Badge bg="light" text="dark">{file.format}</Badge>
                        </td>
                        <td>
                          {file.duration_human || (
                            <span className="text-muted">--:--</span>
                          )}
                        </td>
                        <td className="text-muted">
                          <small>{file.size_human}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          {playingFile && (
            <div className="me-auto text-muted">
              <i className="bi bi-volume-up-fill me-2 text-primary"></i>
              <small>Wiedergabe: {playingFile}</small>
            </div>
          )}
          <Button variant="secondary" onClick={closeFilesModal}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
